import React, {useEffect, useState} from 'react';
import {Lock} from "lucide-react";
import CircleProgress from "../../components/CircleProgress";
import CandidateAssessmentDetails from "./CandidateAssessmentDetails";

const TABS = {
    RoleMatch: 'role-match',
    HireConfidence: 'hire-confidence',
};

const TAB_EVENT = 'CandidateFitTabs@changed';

/**
 * Selecting a tab on one tile selects it on every tile, so a client can scan a
 * whole list of candidates on the same measure. Held outside React so tiles
 * that mount later (pagination, filtering) pick up the current selection.
 */
let sharedTab;

const useSharedFitTab = () => {
    const [tab, setTab] = useState(() => sharedTab || TABS.RoleMatch);

    useEffect(() => {
        const handler = (nextTab) => setTab(nextTab);

        TB.event().provider().on(TAB_EVENT, handler);

        // Adopt a selection that was made before this tile mounted.
        if (sharedTab) {
            setTab(sharedTab);
        }

        return () => TB.event().provider().off(TAB_EVENT, handler);
    }, []);

    const selectTab = (nextTab) => {
        sharedTab = nextTab;
        TB.event(TAB_EVENT, nextTab);
    };

    return [tab, selectTab];
};

/**
 * Where the candidate currently sits in the invite flow, shown above the
 * locked Hire Confidence panel. Null when they have no status worth calling
 * out yet (e.g. an uninvited search result).
 */
const lockedStatusLabel = (candidate) => {
    if (candidate?.isPendingShortlisted?.()) {
        return 'INVITATION SENT';
    }

    const label = candidate?.statusLabel?.();

    return label ? String(label).toUpperCase() : null;
};

const TabButton = ({label, isActive, isMuted, onClick}) => (
    <button
        type="button"
        onClick={(e) => {
            e.stopPropagation();
            onClick();
        }}
        className={TB.mapClass(
            'pb-1 text-[0.7rem] font-semibold tracking-widest border-b-2 transition-colors',
            {
                // An unassessed candidate can still be switched to Hire
                // Confidence — it explains what unlocks it — but reads muted.
                'border-gray-400 text-gray-400': isActive && isMuted,
                'border-black text-black': isActive && !isMuted,
                'border-transparent text-gray-400': !isActive,
            }
        )}
    >
        {label}
    </button>
);

const SubScore = ({label, value}) => (
    <div className="flex flex-row items-center justify-center gap-2">
        <p className="m-0 text-sm text-gray-600">{label}</p>
        <span className="rounded bg-gray-100 px-2 py-0.5 text-sm font-semibold">
            {value}
        </span>
    </div>
);

/**
 * The scoring block on a talent/candidate tile.
 *
 * "Role Match" is the profile-against-role score that has always been shown
 * here. "Hire Confidence" blends that with the AI Engagement Fit assessment of
 * the candidate's proposal, and only unlocks once they have been assessed.
 */
const CandidateFitTabs = ({candidate, onShowMatchDetails}) => {

    const hasEngagementFit = candidate?.hasEngagementFit?.();
    const [activeTab, setActiveTab, canSeeHireConfidence] = useSharedFitTab();

    if (!candidate?.hasMatching?.()) {
        return null;
    }

    const showAssessment = (e) => {
        e.stopPropagation();
        return TB.event('Modal@show', {
            modalClass: 'match-details-modal',
            closeOnBackdrop: true,
            content: <CandidateAssessmentDetails candidate={candidate}/>
        });
    };

    const performance = candidate.performanceSummary?.();
    const statusLabel = lockedStatusLabel(candidate);

    return (
        <div className="candidate-fit-tabs flex flex-col items-center gap-3 w-full">

            <div className="flex flex-row items-center justify-center gap-6">
                <TabButton
                    label="ROLE MATCH"
                    isActive={activeTab === TABS.RoleMatch}
                    onClick={() => setActiveTab(TABS.RoleMatch)}
                />
                {/* Gated for the staged rollout — see useSharedFitTab. This
                    tile is legacy but still rendered by TalentTile and
                    CandidateTile, so it needs the same gate or the feature
                    leaks to users the redesigned tiles hide it from. */}
                {canSeeHireConfidence && (
                    <TabButton
                        label="HIRE CONFIDENCE"
                        isActive={activeTab === TABS.HireConfidence}
                        isMuted={!hasEngagementFit}
                        onClick={() => setActiveTab(TABS.HireConfidence)}
                    />
                )}
            </div>

            {activeTab === TABS.RoleMatch && (
                <div className="flex flex-col items-center gap-2">
                    <CircleProgress
                        className="metric-circle w-28 mx-auto"
                        progress={candidate.roleMatchScore(false)}
                        strokeWidth={7}
                    >
                        {/* m-0 clears the global `p { margin-bottom: 1em }`,
                            which otherwise pushes the value off-centre inside
                            the ring. */}
                        <p className="m-0">
                            <span className="text-xl">{candidate.roleMatchScore()}%</span>
                            <small className="text-xs">Match</small>
                        </p>
                    </CircleProgress>

                    {onShowMatchDetails && (
                        <button type="button" className="details-link" onClick={(e) => {
                            e.stopPropagation();
                            onShowMatchDetails();
                        }}>
                            More...
                        </button>
                    )}
                </div>
            )}

            {activeTab === TABS.HireConfidence && hasEngagementFit && (
                <div className="flex flex-col items-center gap-2 w-full">
                    <CircleProgress
                        className="metric-circle w-28 mx-auto"
                        progress={candidate.hireConfidenceScore(false)}
                        strokeWidth={7}
                        pathColor="#5F3DB1"
                    >
                        <p className="m-0">
                            <span className="text-xl">{candidate.hireConfidenceScore()}</span>
                        </p>
                    </CircleProgress>

                    <p className="m-0 text-lg font-bold text-center">
                        {candidate.hireConfidenceLabel()}
                    </p>

                    <div className="flex flex-row flex-wrap items-center justify-center gap-x-4 gap-y-1">
                        <SubScore label="Role Match" value={candidate.roleMatchScore()}/>
                        <button
                            type="button"
                            onClick={showAssessment}
                            className="flex flex-row items-center gap-2 hover:underline"
                            title="View full assessment"
                        >
                            <p className="m-0 text-sm text-gray-600">Engagement Fit</p>
                            <span className="rounded bg-gray-100 px-2 py-0.5 text-sm font-semibold">
                                {candidate.engagementFitScore()}
                            </span>
                        </button>
                    </div>

                    {performance && (
                        <div className="flex flex-row flex-wrap items-center justify-center gap-x-3 gap-y-1 border-t pt-2 mt-1 w-full">
                            {performance.badge && (
                                <span className="inline-flex items-center gap-1.5 rounded border px-2 py-1 text-[0.7rem] font-semibold">
                                    <i className="fas fa-trophy"/>
                                    {performance.badge}
                                </span>
                            )}
                            <p className="m-0 text-xs text-gray-600">
                                <strong>{performance.completed_projects_count}</strong>
                                {` ${TB.pluralize('Project', performance.completed_projects_count)} • `}
                                <strong>{performance.unique_clients_count}</strong>
                                {` ${TB.pluralize('Client', performance.unique_clients_count)}`}
                                {!!performance.rehires_count && (
                                    <>
                                        {' • '}
                                        <strong>{performance.rehires_count}</strong>
                                        {` ${TB.pluralize('Rehire', performance.rehires_count)}`}
                                    </>
                                )}
                            </p>
                        </div>
                    )}
                </div>
            )}

            {activeTab === TABS.HireConfidence && !hasEngagementFit && (
                <div className="flex flex-col items-center gap-3 py-3">
                    {statusLabel && (
                        <span className="rounded-full bg-[#F1ECFB] text-primary px-3 py-1.5 text-[0.65rem] font-semibold tracking-widest">
                            {statusLabel}
                        </span>
                    )}

                    <Lock className="h-7 w-7 text-primary-lighter" strokeWidth={1.75}/>

                    <div className="flex flex-col items-center gap-1">
                        <p className="m-0 font-bold text-base">Hire Confidence</p>
                        <p className="m-0 text-sm text-gray-500 text-center">
                            Unlocks when {candidate.first_name || 'this candidate'} submits a proposal
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CandidateFitTabs;
export {TABS};
